import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityWidget.css';

const DEFAULT_SETTINGS = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

const STORAGE_KEY = 'a11y-settings';

function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const applySettings = useCallback((s) => {
    const html = document.documentElement;

    // Font size
    const base = 100 + s.fontSize * 10;
    html.style.fontSize = base === 100 ? '' : `${base}%`;

    // Toggle classes
    html.classList.toggle('a11y-high-contrast', s.highContrast);
    html.classList.toggle('a11y-highlight-links', s.highlightLinks);
    html.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);
    html.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage unavailable
    }
  }, [settings, applySettings]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetAll = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const adjustFontSize = (delta) => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.max(-3, Math.min(5, prev.fontSize + delta)),
    }));
  };

  return (
    <div className="a11y-widget">
      <button
        ref={buttonRef}
        className="a11y-widget__toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Accessibility options"
        aria-expanded={isOpen}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="a11y-widget__panel"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <h3 className="a11y-widget__title">Accessibility</h3>

          {/* Font Size */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(-1)}
                aria-label="Decrease font size"
                disabled={settings.fontSize <= -3}
              >
                A&minus;
              </button>
              <span className="a11y-widget__font-value">
                {settings.fontSize === 0 ? 'Default' : `${settings.fontSize > 0 ? '+' : ''}${settings.fontSize}`}
              </span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(1)}
                aria-label="Increase font size"
                disabled={settings.fontSize >= 5}
              >
                A+
              </button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">High Contrast</span>
            <button
              className={`a11y-widget__switch ${settings.highContrast ? 'a11y-widget__switch--active' : ''}`}
              onClick={() => updateSetting('highContrast', !settings.highContrast)}
              role="switch"
              aria-checked={settings.highContrast}
              aria-label="Toggle high contrast"
            >
              <span className="a11y-widget__switch-knob" />
            </button>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Highlight Links</span>
            <button
              className={`a11y-widget__switch ${settings.highlightLinks ? 'a11y-widget__switch--active' : ''}`}
              onClick={() => updateSetting('highlightLinks', !settings.highlightLinks)}
              role="switch"
              aria-checked={settings.highlightLinks}
              aria-label="Toggle highlight links"
            >
              <span className="a11y-widget__switch-knob" />
            </button>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <button
              className={`a11y-widget__switch ${settings.dyslexiaFont ? 'a11y-widget__switch--active' : ''}`}
              onClick={() => updateSetting('dyslexiaFont', !settings.dyslexiaFont)}
              role="switch"
              aria-checked={settings.dyslexiaFont}
              aria-label="Toggle dyslexia-friendly font"
            >
              <span className="a11y-widget__switch-knob" />
            </button>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Reduce Motion</span>
            <button
              className={`a11y-widget__switch ${settings.reducedMotion ? 'a11y-widget__switch--active' : ''}`}
              onClick={() => updateSetting('reducedMotion', !settings.reducedMotion)}
              role="switch"
              aria-checked={settings.reducedMotion}
              aria-label="Toggle reduced motion"
            >
              <span className="a11y-widget__switch-knob" />
            </button>
          </div>

          {/* Reset */}
          <button
            className="a11y-widget__reset"
            onClick={resetAll}
            aria-label="Reset all accessibility settings"
          >
            Reset All
          </button>
        </div>
      )}
    </div>
  );
}

export default AccessibilityWidget;
